import { type QueryKey } from '@tanstack/react-query';
import { type MouseEvent, type ReactElement, useCallback } from 'react';
import { type CoverageArtifactRow } from '@/features/codeCoverage/pages/coverageArtifactRows';
import { CoverageFilesTable } from '@/features/codeCoverage/pages/CoverageFilesTable';
import {
  coverageFilesTableDefinition,
  type CoverageFilesSortKey,
} from '@/features/codeCoverage/pages/coverageFilesTableDefinition';
import { CoverageRunsTable } from '@/features/codeCoverage/pages/CoverageRunsTable';
import {
  coverageRunsTableDefinition,
  type CoverageRunsSortKey,
} from '@/features/codeCoverage/pages/coverageRunsTableDefinition';
import {
  uploadsTableDefinition,
  type UploadsSortKey,
} from '@/features/executionRuns/pages/uploadsTableDefinition';
import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { tablePanelSurfaceSx } from '@/features/shared/components/tableSystem/tablePanelSurfaceSx';
import {
  tablePanelTabSx,
  tablePanelTabsSx,
} from '@/features/shared/components/tableSystem/tablePanelTabs';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import { type DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import { UploadsTable } from '@/features/uploads/components/UploadsTable';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { Box, Panel } from '@/ui';
import { Stack } from '@/ui/layout/Stack';
import { type GridColumnVisibilityModel } from '@/ui/primitives';
import { Tab, Tabs } from '@/ui/primitives/Tabs';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { CoverageRunDto, QueryResult, UploadDto, UploadStatus } from '@flowqa/shared';
import type { Dayjs } from 'dayjs';

export type CoverageTableTab = 'coverage-runs' | 'coverage-files' | 'uploads';

type CoverageTablesPanelProps = {
  activeTableTab: CoverageTableTab;
  coverageFilesColumnVisibilityModel: GridColumnVisibilityModel;
  coverageFilesLoading?: boolean;
  coverageFilesSortDirection: 'asc' | 'desc';
  coverageFilesSortKey: CoverageFilesSortKey;
  coverageRunsLoading?: boolean;
  coverageRunsColumnVisibilityModel: GridColumnVisibilityModel;
  coverageRunsSortDirection: 'asc' | 'desc';
  coverageRunsSortKey: CoverageRunsSortKey;
  dateQuickFilter: DateQuickFilter;
  endDay: string | null;
  files: CoverageArtifactRow[];
  filesQueryKey: QueryKey;
  fromDate: Dayjs | null;
  onCoverageFilesColumnVisibilityModelChange: (nextModel: GridColumnVisibilityModel) => void;
  onCoverageFilesSortChange: (
    nextSortKey: CoverageFilesSortKey,
    nextSortDirection: 'asc' | 'desc',
  ) => void;
  onCoverageRunsColumnVisibilityModelChange: (nextModel: GridColumnVisibilityModel) => void;
  onCoverageRunsSortChange: (
    nextSortKey: CoverageRunsSortKey,
    nextSortDirection: 'asc' | 'desc',
  ) => void;
  onTableTabChange: (nextTab: CoverageTableTab) => void;
  onToDateChange: (nextDate: Dayjs | null) => void;
  onDateQuickFilterChange: (nextQuickFilter: DateQuickFilter) => void;
  onFromDateChange: (nextDate: Dayjs | null) => void;
  onUploadsColumnVisibilityModelChange: (nextModel: GridColumnVisibilityModel) => void;
  onUploadsSortChange: (nextSortKey: UploadsSortKey, nextSortDirection: 'asc' | 'desc') => void;
  runs: CoverageRunDto[];
  runsQueryKey: QueryKey;
  selectedProjectId: string | null;
  startDay: string | null;
  toDate: Dayjs | null;
  uploadsColumnVisibilityModel: GridColumnVisibilityModel;
  uploadsFilters: GridQueryInput['filters'];
  uploadsQueryFn: (input: GridQueryInput) => Promise<QueryResult<UploadDto>>;
  uploadsQueryKey: QueryKey;
  uploadsSortDirection: 'asc' | 'desc';
  uploadsSortKey: UploadsSortKey;
};

const EMPTY_SEARCH = '';
const EMPTY_TAGS: string[] = [];
const EMPTY_STATUSES: Array<'unused'> = [];
const EMPTY_STATUS_OPTIONS: Array<{ value: 'unused'; label: string }> = [];
const EMPTY_UPLOAD_STATUSES: UploadStatus[] = [];

const buildInitialSortModel = (
  field: string,
  direction: 'asc' | 'desc',
): Array<{ field: string; sort: 'asc' | 'desc' }> => [{ field, sort: direction }];

const buildTabPanelSx = (active: boolean) =>
  ({
    minWidth: 0,
    display: active ? 'block' : 'none',
  }) as const;

export const CoverageTablesPanel = ({
  activeTableTab,
  coverageFilesColumnVisibilityModel,
  coverageFilesLoading = false,
  coverageFilesSortDirection,
  coverageFilesSortKey,
  coverageRunsLoading = false,
  coverageRunsColumnVisibilityModel,
  coverageRunsSortDirection,
  coverageRunsSortKey,
  dateQuickFilter,
  endDay,
  files,
  filesQueryKey,
  fromDate,
  onCoverageFilesColumnVisibilityModelChange,
  onCoverageFilesSortChange,
  onCoverageRunsColumnVisibilityModelChange,
  onCoverageRunsSortChange,
  onDateQuickFilterChange,
  onFromDateChange,
  onTableTabChange,
  onToDateChange,
  onUploadsColumnVisibilityModelChange,
  onUploadsSortChange,
  runs,
  runsQueryKey,
  selectedProjectId,
  startDay,
  toDate,
  uploadsColumnVisibilityModel,
  uploadsFilters,
  uploadsQueryFn,
  uploadsQueryKey,
  uploadsSortDirection,
  uploadsSortKey,
}: CoverageTablesPanelProps): ReactElement => {
  const handleTabMouseDown = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const activeToolbar = (() => {
    switch (activeTableTab) {
      case 'coverage-runs':
        return (
          <TableToolbar<CoverageRunsSortKey, 'unused'>
            title={undefined}
            searchAriaLabel={coverageRunsTableDefinition.searchAriaLabel}
            searchValue={EMPTY_SEARCH}
            onDebouncedSearchChange={() => undefined}
            columnVisibilityModel={coverageRunsColumnVisibilityModel}
            defaultColumnVisibilityModel={coverageRunsTableDefinition.defaultColumnVisibilityModel}
            columnOptions={coverageRunsTableDefinition.columnOptions}
            onColumnVisibilityModelChange={onCoverageRunsColumnVisibilityModelChange}
            sortKey={coverageRunsSortKey}
            sortDirection={coverageRunsSortDirection}
            defaultSortKey={coverageRunsTableDefinition.defaultSortKey}
            defaultSortDirection={coverageRunsTableDefinition.defaultSortDirection}
            sortOptions={coverageRunsTableDefinition.sortOptions}
            onSortChange={onCoverageRunsSortChange}
            filterStatuses={EMPTY_STATUSES}
            statusOptions={EMPTY_STATUS_OPTIONS}
            onFilterStatusesChange={() => undefined}
            filterTags={EMPTY_TAGS}
            onFilterTagsChange={() => undefined}
            dateQuickFilter={dateQuickFilter}
            onDateQuickFilterChange={onDateQuickFilterChange}
            fromDate={fromDate}
            toDate={toDate}
            latestQueryStartDay={startDay}
            latestQueryEndDay={endDay}
            onFromDateChange={onFromDateChange}
            onToDateChange={onToDateChange}
            availableTags={EMPTY_TAGS}
            hideDateRangeLabel
            showControlsWithoutSummary
          />
        );
      case 'coverage-files':
        return (
          <TableToolbar<CoverageFilesSortKey, 'unused'>
            title={undefined}
            searchAriaLabel={coverageFilesTableDefinition.searchAriaLabel}
            searchValue={EMPTY_SEARCH}
            onDebouncedSearchChange={() => undefined}
            columnVisibilityModel={coverageFilesColumnVisibilityModel}
            defaultColumnVisibilityModel={coverageFilesTableDefinition.defaultColumnVisibilityModel}
            columnOptions={coverageFilesTableDefinition.columnOptions}
            onColumnVisibilityModelChange={onCoverageFilesColumnVisibilityModelChange}
            sortKey={coverageFilesSortKey}
            sortDirection={coverageFilesSortDirection}
            defaultSortKey={coverageFilesTableDefinition.defaultSortKey}
            defaultSortDirection={coverageFilesTableDefinition.defaultSortDirection}
            sortOptions={coverageFilesTableDefinition.sortOptions}
            onSortChange={onCoverageFilesSortChange}
            filterStatuses={EMPTY_STATUSES}
            statusOptions={EMPTY_STATUS_OPTIONS}
            onFilterStatusesChange={() => undefined}
            filterTags={EMPTY_TAGS}
            onFilterTagsChange={() => undefined}
            dateQuickFilter={dateQuickFilter}
            onDateQuickFilterChange={onDateQuickFilterChange}
            fromDate={fromDate}
            toDate={toDate}
            latestQueryStartDay={startDay}
            latestQueryEndDay={endDay}
            onFromDateChange={onFromDateChange}
            onToDateChange={onToDateChange}
            availableTags={EMPTY_TAGS}
            hideDateRangeLabel
            showControlsWithoutSummary
          />
        );
      case 'uploads':
      default:
        return (
          <TableToolbar<UploadsSortKey, UploadStatus>
            title={undefined}
            searchAriaLabel={uploadsTableDefinition.searchAriaLabel}
            searchValue={EMPTY_SEARCH}
            onDebouncedSearchChange={() => undefined}
            columnVisibilityModel={uploadsColumnVisibilityModel}
            defaultColumnVisibilityModel={uploadsTableDefinition.defaultColumnVisibilityModel}
            columnOptions={uploadsTableDefinition.columnOptions}
            onColumnVisibilityModelChange={onUploadsColumnVisibilityModelChange}
            sortKey={uploadsSortKey}
            sortDirection={uploadsSortDirection}
            defaultSortKey={uploadsTableDefinition.defaultSortKey}
            defaultSortDirection={uploadsTableDefinition.defaultSortDirection}
            sortOptions={uploadsTableDefinition.sortOptions}
            onSortChange={onUploadsSortChange}
            filterStatuses={EMPTY_UPLOAD_STATUSES}
            statusOptions={uploadsTableDefinition.statusOptions}
            onFilterStatusesChange={() => undefined}
            filterTags={EMPTY_TAGS}
            onFilterTagsChange={() => undefined}
            dateQuickFilter={dateQuickFilter}
            onDateQuickFilterChange={onDateQuickFilterChange}
            fromDate={fromDate}
            toDate={toDate}
            latestQueryStartDay={startDay}
            latestQueryEndDay={endDay}
            onFromDateChange={onFromDateChange}
            onToDateChange={onToDateChange}
            availableTags={EMPTY_TAGS}
            hideDateRangeLabel
            showControlsWithoutSummary
          />
        );
    }
  })();

  return (
    <Panel
      padding="md"
      gap="none"
      contentGap="md"
      headerGap="none"
      headerSlot={
        <Box
          sx={{
            borderBottom: 'none',
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            position: 'relative',
            '&::after': buildTableDividerPseudoSx('bottom'),
          }}
        >
          <Stack
            direction="row"
            align="flex-end"
            justify="space-between"
            gap="sm"
            sx={{
              minWidth: 0,
              minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Tabs
                value={activeTableTab}
                onChange={(_event, nextValue) => {
                  onTableTabChange(nextValue as CoverageTableTab);
                }}
                preserveScrollOnChange={false}
                aria-label="Coverage tables"
                sx={tablePanelTabsSx}
              >
                <Tab
                  id="coverage-runs-tab"
                  label="Coverage Runs"
                  value="coverage-runs"
                  onMouseDown={handleTabMouseDown}
                  sx={tablePanelTabSx}
                />
                <Tab
                  id="coverage-files-tab"
                  label="Coverage Files"
                  value="coverage-files"
                  onMouseDown={handleTabMouseDown}
                  sx={tablePanelTabSx}
                />
                <Tab
                  id="uploads-tab"
                  label="Uploads"
                  value="uploads"
                  onMouseDown={handleTabMouseDown}
                  sx={tablePanelTabSx}
                />
              </Tabs>
            </Box>
            <Box sx={{ minWidth: 0, flex: '0 0 auto' }}>{activeToolbar}</Box>
          </Stack>
        </Box>
      }
      sx={tablePanelSurfaceSx}
    >
      <Stack gap="md">
        <Box
          role="tabpanel"
          id="coverage-runs-panel"
          aria-labelledby="coverage-runs-tab"
          aria-hidden={activeTableTab !== 'coverage-runs'}
          sx={buildTabPanelSx(activeTableTab === 'coverage-runs')}
        >
          <Box>
            <CoverageRunsTable
              runs={runs}
              queryKey={runsQueryKey}
              projectId={selectedProjectId}
              loading={coverageRunsLoading}
              initialSortModel={buildInitialSortModel(
                coverageRunsTableDefinition.sortFieldMap[coverageRunsSortKey],
                coverageRunsSortDirection,
              )}
              columnVisibilityModel={coverageRunsColumnVisibilityModel}
              onColumnVisibilityModelChange={onCoverageRunsColumnVisibilityModelChange}
            />
          </Box>
        </Box>
        <Box
          role="tabpanel"
          id="coverage-files-panel"
          aria-labelledby="coverage-files-tab"
          aria-hidden={activeTableTab !== 'coverage-files'}
          sx={buildTabPanelSx(activeTableTab === 'coverage-files')}
        >
          <Box>
            <CoverageFilesTable
              files={files}
              queryKey={filesQueryKey}
              projectId={selectedProjectId}
              loading={coverageFilesLoading}
              initialSortModel={buildInitialSortModel(
                coverageFilesTableDefinition.sortFieldMap[coverageFilesSortKey],
                coverageFilesSortDirection,
              )}
              columnVisibilityModel={coverageFilesColumnVisibilityModel}
              onColumnVisibilityModelChange={onCoverageFilesColumnVisibilityModelChange}
            />
          </Box>
        </Box>
        <Box
          role="tabpanel"
          id="uploads-panel"
          aria-labelledby="uploads-tab"
          aria-hidden={activeTableTab !== 'uploads'}
          sx={buildTabPanelSx(activeTableTab === 'uploads')}
        >
          <UploadsTable
            persistKey="coverage-uploads-table-v2"
            fixedFilters={uploadsFilters}
            queryFn={uploadsQueryFn}
            queryKey={uploadsQueryKey}
            pageSizeOptions={[5, 10, 25, 50]}
            initialPageSize={5}
            disableColumnSorting
            disableColumnMenu
            noRowsText={getEmptyStateMessage('coverage.noUploadsFound')}
            initialSortModel={buildInitialSortModel(
              uploadsTableDefinition.sortFieldMap[uploadsSortKey],
              uploadsSortDirection,
            )}
            columnVisibilityModel={uploadsColumnVisibilityModel}
            onColumnVisibilityModelChange={onUploadsColumnVisibilityModelChange}
          />
        </Box>
      </Stack>
    </Panel>
  );
};
